import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Bell,
  CreditCard,
  MonitorCog,
  Settings,
  ShieldCheck,
  UserRound,
  Users,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NotificationsTab } from "@/components/settings/notifications-tab";
import { AlertPolicyTab } from "@/components/settings/alert-policy-tab";
import { BrandingTab } from "@/components/settings/branding-tab";
import { BillingTab } from "@/components/settings/billing-tab";
import { UsersTab } from "@/components/settings/users-tab";
import { AgentsTab } from "@/components/settings/agents-tab";
import { AccountTab } from "@/components/settings/account-tab";
import { FleetDashboardTab } from "@/components/settings/fleet-dashboard-tab";
import { ManifestHistoryTab } from "@/components/settings/manifest-history-tab";
import { AgentVersionMgmtTab } from "@/components/settings/agent-version-mgmt-tab";
import { RolloutStatusTab } from "@/components/settings/rollout-status-tab";
import { AgentActivityTab } from "@/components/settings/agent-activity-tab";
import { EnrichmentTab } from "@/components/settings/enrichment-tab";

// MVP launch: tenant portal trimmed to the essentials. Hidden tabs' content
// blocks + backend stay intact (reachable by direct tab value); re-enable by
// uncommenting. Branding + Fleet are MSP-portal concerns (no MSP customers yet);
// Manifests/Versions/Rollouts are agent-update ops; Alert Policy is incomplete
// (#665); AI Enrichment exposes the internal provider/model.
interface SettingsTab {
  value: string;
  label: string;
  description: string;
  icon: LucideIcon;
}

interface SettingsGroup {
  label: string;
  tabs: SettingsTab[];
}

const settingsGroups: SettingsGroup[] = [
  {
    label: "Organization",
    tabs: [
      {
        value: "users",
        label: "Users",
        description: "Invite people and manage team roles.",
        icon: Users,
      },
    ],
  },
  {
    label: "Detection & data",
    tabs: [
      {
        value: "notifications",
        label: "Notifications",
        description: "Configure alert delivery channels and thresholds.",
        icon: Bell,
      },
      {
        value: "agents",
        label: "Agents",
        description: "Install agents and manage enrollment keys.",
        icon: MonitorCog,
      },
    ],
  },
  {
    label: "Billing",
    tabs: [
      {
        value: "billing",
        label: "Plan & usage",
        description: "Review your subscription, limits, and usage.",
        icon: CreditCard,
      },
    ],
  },
  {
    label: "Personal",
    tabs: [
      {
        value: "account",
        label: "Account",
        description: "Manage your password and account security.",
        icon: UserRound,
      },
    ],
  },
];

const tabs = settingsGroups.flatMap((group) => group.tabs);

const settingsSectionAliases: Record<string, string> = {
  notifications: "notifications",
  alerts: "notifications",
  workspace: "notifications",
  billing: "billing",
  users: "users",
  members: "users",
  roles: "users",
  agents: "agents",
  account: "account",
};

export default function SettingsPage() {
  const { settingsSection } = useParams<{ settingsSection?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const routeTab = settingsSectionAliases[settingsSection ?? ""] ?? "notifications";
  const [activeTab, setActiveTab] = useState(routeTab);
  const active = tabs.find((tab) => tab.value === activeTab) ?? tabs[0];
  const ActiveIcon = active.icon;

  useEffect(() => {
    setActiveTab(routeTab);
  }, [routeTab]);

  function selectTab(value: string) {
    setActiveTab(value);
    const settingsIndex = location.pathname.indexOf("/settings");
    if (settingsIndex === -1) return;
    const settingsBase = location.pathname.slice(0, settingsIndex + "/settings".length);
    navigate(`${settingsBase}/${value}${location.search}${location.hash}`);
  }

  return (
    <div className="mx-auto w-full max-w-[1500px]" data-testid="production-settings">
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-primary/25 bg-primary/10">
          <Settings className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">Settings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage your workspace, detection, team, and account
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={selectTab}>
        <label className="mb-4 block lg:hidden" htmlFor="settings-section">
          <span className="mb-1.5 block text-xs font-medium text-muted-foreground">
            Settings section
          </span>
          <select
            id="settings-section"
            value={activeTab}
            onChange={(event) => selectTab(event.target.value)}
            className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {settingsGroups.map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.tabs.map((tab) => (
                  <option key={tab.value} value={tab.value}>
                    {tab.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
        </label>

        <div className="grid gap-4 lg:grid-cols-[250px_minmax(0,1fr)]">
          <TabsList
            aria-label="Settings navigation"
            className="hidden h-auto w-full flex-col items-stretch justify-start rounded-xl border border-border bg-card p-2 lg:flex"
          >
            <div className="flex items-center gap-2 px-3 pb-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5" />
              Workspace settings
            </div>
            {settingsGroups.map((group) => (
              <div key={group.label} className="mb-4 last:mb-0">
                <p className="mb-1 px-3 text-left text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {group.label}
                </p>
                <div className="space-y-0.5">
                  {group.tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <TabsTrigger
                        key={tab.value}
                        value={tab.value}
                        className="flex w-full justify-start gap-3 rounded-lg px-3 py-2.5 text-muted-foreground shadow-none data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
                      >
                        <Icon className="h-[18px] w-[18px] shrink-0" />
                        <span>{tab.label}</span>
                      </TabsTrigger>
                    );
                  })}
                </div>
              </div>
            ))}
          </TabsList>

          <section
            className="min-w-0 rounded-xl border border-border bg-card p-4 sm:p-5 lg:p-6"
            aria-labelledby="settings-section-title"
          >
            <div className="mb-5 border-b border-border pb-5">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-primary/25 bg-primary/10">
                  <ActiveIcon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h2 id="settings-section-title" className="text-xl font-semibold sm:text-2xl">
                    {active.label}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">{active.description}</p>
                </div>
              </div>
            </div>

            <TabsContent value="notifications" className="mt-0">
              <NotificationsTab />
            </TabsContent>

            <TabsContent value="alert-policy" className="mt-0">
              <AlertPolicyTab />
            </TabsContent>

            <TabsContent value="branding" className="mt-0">
              <BrandingTab />
            </TabsContent>

            <TabsContent value="billing" className="mt-0">
              <BillingTab />
            </TabsContent>

            <TabsContent value="users" className="mt-0">
              <UsersTab />
            </TabsContent>

            <TabsContent value="agents" className="mt-0">
              <AgentsTab />
            </TabsContent>

            <TabsContent value="fleet" className="mt-0">
              <FleetDashboardTab />
            </TabsContent>

            <TabsContent value="manifests" className="mt-0">
              <ManifestHistoryTab />
            </TabsContent>

            <TabsContent value="versions" className="mt-0">
              <AgentVersionMgmtTab />
            </TabsContent>

            <TabsContent value="rollouts" className="mt-0">
              <RolloutStatusTab />
            </TabsContent>

            <TabsContent value="activity" className="mt-0">
              <AgentActivityTab />
            </TabsContent>

            <TabsContent value="enrichment" className="mt-0">
              <EnrichmentTab />
            </TabsContent>

            <TabsContent value="account" className="mt-0">
              <AccountTab />
            </TabsContent>
          </section>
        </div>
      </Tabs>
    </div>
  );
}
